import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { createDateAxis, createValueAxis } from "../TrendsUtils";
import Chart from "../../../../Components/Chart/Chart";
import useChart from "../../../../Components/Chart/useChart";
import useTrend from "../useTrend";
import { AppPreferencesContext } from "../../../../General/AppPreferencesProvider";
import type { SelectedScope } from "../../../../types/scope";

// Status -> display metadata maps. Indexed by status keys coming from the trend
// rows (strings), so reads through them are typed as a string-keyed record.
const TicketStatusDetails: Record<string, { fill: string; title: string }> = {
  BACKLOG: { fill: "#8DC9F3", title: "Backlog" },
  SCHEDULED: { fill: "#5565F9", title: "Scheduled" },
  IN_PROGRESS: { fill: "#A673F1", title: "In Progress" },
  DONE: { fill: "#21BC89", title: "Done" },
  REJECTED: { fill: "#EC2A3F", title: "Rejected" },
};

const OpenClosedDetails: Record<string, { fill: string; title: string }> = {
  OPEN: { fill: "#8DC9F3", title: "Open" },
  CLOSED: { fill: "#21BC89", title: "Closed" },
};

// Single point in a chart series, derived from each trend row.
interface TicketsByStatusChartDatum {
  date: string;
  value: number;
  dashLength?: number;
}

// trendData state is keyed dynamically by status (OPEN/CLOSED in the new-trends
// path, or ticket-status strings otherwise); each entry feeds one series.
type TicketsByStatusTrendData = Record<string, TicketsByStatusChartDatum[]>;

// Raw trend rows are tuples coming from parsed JSON; trendData from useTrend is
// typed `unknown`, so we narrow locally. In the new-trends path the row is
// `[date, openCount, closedCount]`; otherwise `[date, status, count]`.
type NewTrendRow = [string, number, number];
type StatusTrendRow = [string, string, number];
type TrendRow = NewTrendRow | StatusTrendRow;

export interface TicketsByStatusProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  timeScale: string;
  scopeToExecute?: string | null;
  id: string | number;
  widgetKey?: string;
  setIsReadyToExport?: ((isReady: boolean) => void) | null;
}

const TicketsByStatus: React.FC<TicketsByStatusProps> = ({
  selectedScope,
  filterToExecute,
  timeScale,
  scopeToExecute,
  id,
  widgetKey,
  setIsReadyToExport = null,
}) => {
  const [max, setMax] = useState(0);
  const chartName = `ticketsByStatusTrend-${id}`;
  const {
    appPreferences: { useNewTrends },
  } = useContext(AppPreferencesContext);

  const {
    trendData: data,
    trendDataError,
    trendLoading,
    refetchTrend,
  } = useTrend({
    variables: {
      ...(widgetKey ? { widgetKey } : {}),
      timescale: timeScale,
      selection: ["ticket_id"],
      group_by: ["ticket_status"],
      aggregate_functions: ["COUNT"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute,
        scopesid: selectedScope?.value?.id,
        filters_fields_to_exclude: ["ticket_status", "actual_status"],
      },
      snapshots_filters_config: {
        filtersjson: filterToExecute,
        filters_fields_to_exclude: ["ticket_id", "ticket_status"],
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });
  const [trendData, setTrendData] = useState<TicketsByStatusTrendData | null>(
    null
  );
  const {
    chartLoading,
    dataError,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart(setIsReadyToExport);

  const clearData = () => {
    setTrendData(null);
  };

  useEffect(() => {
    if (!!trendLoading) {
      startLoadingChart();
      clearData();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [trendLoading, setChartDataExists, setChartError, startLoadingChart]);

  useEffect(() => {
    if (!!trendDataError) {
      setChartError(true);
      stopLoadingChart();
    }
  }, [trendDataError, setChartError, stopLoadingChart]);

  useEffect(() => {
    if (!!data) {
      const rows = data as TrendRow[];
      const dataObj: TicketsByStatusTrendData = {};

      for (let i = 0; i < rows.length; i += 1) {
        if (useNewTrends) {
          const [date, openTicketsCount, closedTicketsCount] = rows[
            i
          ] as NewTrendRow;

          if (!dataObj.OPEN) {
            dataObj.OPEN = [];
          }
          if (!dataObj.CLOSED) {
            dataObj.CLOSED = [];
          }

          dataObj.OPEN.push({
            date: date,
            value: openTicketsCount,
          });
          dataObj.CLOSED.push({
            date: date,
            value: closedTicketsCount,
          });
        } else {
          const [date, status, count] = rows[i] as StatusTrendRow;

          if (!!status) {
            if (!dataObj.hasOwnProperty(status)) {
              dataObj[status] = [];
            }

            dataObj[status].push({
              date: date,
              value: count,
            });
          }
        }
      }

      if (
        !dataObj ||
        !Object.values(dataObj).find((obj) =>
          obj.find((item) => item.value !== 0)
        )
      ) {
        stopLoadingChart();
        setChartDataExists(false);
      } else if (Object.keys(dataObj)?.length) {
        setChartError(false);
        stopLoadingChart();
        setChartDataExists(true);
      }

      Object.keys(dataObj).forEach((item) => {
        const itemData = dataObj[item];
        if (itemData?.length >= 2) {
          itemData[itemData?.length - 2].dashLength = 8;
        }
      });

      let maxNum = 0;
      rows.forEach((item) => {
        item?.forEach((value) => {
          if (typeof value === "number" && value > maxNum) {
            maxNum = value;
          }
        });
      });

      setMax(maxNum * 1.2);
      setTrendData(dataObj);
    }
  }, [data, useNewTrends]);

  useLayoutEffect(() => {
    if (dataExists) {
      stopLoadingChart();

      let [chart, legendContainer] = createChart(chartName, am4charts.XYChart);

      chart.cursor = new am4charts.XYCursor();

      chart.paddingTop = 45;
      chart.paddingBottom = -10;
      chart.paddingRight = 40;

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      createDateAxis(dateAxis, timeScale);
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis, "Tickets", max);
      valueAxis.getSeriesDataItem = function (
        this: am4charts.ValueAxis,
        series: am4charts.XYSeries,
        position: number
      ) {
        const key = this.axisFieldName + this.axisLetter;
        const value = this.positionToValue(position);
        const dataItem = series.dataItems.getIndex(
          series.dataItems.findClosestIndex(
            value,
            (x) => {
              return (x as unknown as Record<string, number>)[key]
                ? (x as unknown as Record<string, number>)[key]
                : (undefined as unknown as number);
            },
            "any"
          )
        );
        return dataItem;
      } as typeof valueAxis.getSeriesDataItem;

      let label = dateAxis.renderer.labels.template;
      label.wrap = true;
      label.maxWidth = 85;
      label.textAlign = "middle";

      if (trendData) {
        for (const [key] of Object.entries(trendData).sort(
          (a, b) =>
            // Original arithmetic on string keys: yields NaN for non-numeric
            // status keys (effectively a stable no-op). Preserved exactly.
            (b[0] as unknown as number) - (a[0] as unknown as number)
        )) {
          if (useNewTrends) {
            createSeries(key, OpenClosedDetails[key]?.title);
          } else if (!!TicketStatusDetails[key]) {
            createSeries(key, TicketStatusDetails[key]?.title);
          }
        }
      }

      function createSeries(s: string, name: string | undefined) {
        let series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = "value";
        series.dataFields.dateX = "date";
        series.name = name as string;
        series.tensionX = 0.8;
        series.strokeWidth = 2;
        series.stroke = useNewTrends
          ? am4core.color(OpenClosedDetails[s]?.fill)
          : am4core.color(TicketStatusDetails[s]?.fill);
        series.fill = useNewTrends
          ? am4core.color(OpenClosedDetails[s]?.fill)
          : am4core.color(TicketStatusDetails[s]?.fill);
        series.strokeOpacity = 0.6;
        series.tooltipText = "{name}: {value}";
        series.tooltip!.autoTextColor = false;

        const bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.strokeWidth = 2;
        bullet.circle.radius = 3;

        let segment = series.segments.template;
        segment.interactionsEnabled = true;

        segment.events.on("over", function (event) {
          processOver(
            event.target.parent!.parent!
              .parent as unknown as am4charts.LineSeries
          );
        });

        segment.events.on("out", function (event) {
          processOut(
            event.target.parent!.parent!
              .parent as unknown as am4charts.LineSeries
          );
        });

        series.data = trendData?.[s] as TicketsByStatusChartDatum[];
        series.propertyFields.strokeDasharray = "dashLength";

        return series;
      }

      chart.legend.itemContainers.template.events.on("over", function (event) {
        processOver(
          event.target.dataItem!.dataContext as unknown as am4charts.LineSeries
        );
      });

      chart.legend.itemContainers.template.events.on("out", function (event) {
        processOut(
          event.target.dataItem!.dataContext as unknown as am4charts.LineSeries
        );
      });

      function processOver(hoveredSeries: am4charts.LineSeries) {
        hoveredSeries.toFront();

        hoveredSeries.segments.each(function (segment) {
          segment.setState("hover");
        });

        chart.series.each(function (series) {
          if (series !== hoveredSeries) {
            (series as am4charts.LineSeries).segments.each(function (segment) {
              segment.setState("dimmed");
            });
            series.bulletsContainer.setState("dimmed");
          }
        });
      }

      function processOut(hoveredSeries: am4charts.LineSeries) {
        chart.series.each(function (series) {
          (series as am4charts.LineSeries).segments.each(function (segment) {
            segment.setState("default");
          });
          series.bulletsContainer.setState("default");
        });
      }

      return () => {
        chart.dispose();
        legendContainer?.dispose();
      };
    }
  }, [trendData, dataExists, createChart]);

  return (
    <Chart
      chartLoading={chartLoading}
      chartName={chartName}
      chartError={dataError}
      chartData={dataExists}
      chartReload={refetchTrend}
    />
  );
};

export default TicketsByStatus;
